import { useEffect, useRef, useState } from 'react';

interface HeroMediaProps {
  posterSrc: string;
  posterAlt?: string;
  videoSrc?: string;
  className?: string;
}

/**
 * Renders the hero poster image immediately for fast LCP.
 * If a videoSrc is provided AND a HEAD probe succeeds (non-503/404),
 * the video is mounted on top, fades in once it can play, and quietly
 * unmounts on any playback/network error — falling back to the poster.
 *
 * Resilient by design: the poster is the source of truth and is never
 * removed from the DOM. The video is purely additive.
 */
const HeroMedia = ({ posterSrc, posterAlt = '', videoSrc, className }: HeroMediaProps) => {
  const [videoOk, setVideoOk] = useState(false);
  const [videoVisible, setVideoVisible] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Probe the video URL once, after first paint, to keep LCP unaffected.
  useEffect(() => {
    if (!videoSrc) return;
    // Respect Save-Data and slow-connection signals — mobile users on
    // metered or 2G/3G links should never pay the cost of a hero video.
    type NetInfo = { saveData?: boolean; effectiveType?: string };
    const conn = (navigator as Navigator & { connection?: NetInfo }).connection;
    if (conn?.saveData) return;
    if (conn?.effectiveType && /(^|-)(2g|3g)$/i.test(conn.effectiveType)) return;
    let cancelled = false;

    const probe = async () => {
      try {
        const res = await fetch(videoSrc, { method: 'HEAD', cache: 'no-store' });
        if (!cancelled && res.ok) setVideoOk(true);
      } catch {
        // Network error / CORS / 503 — silently keep poster.
      }
    };

    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    let idleHandle: number | null = null;
    let timeoutHandle: number | null = null;
    if (typeof w.requestIdleCallback === 'function') {
      idleHandle = w.requestIdleCallback(probe, { timeout: 2000 });
    } else {
      timeoutHandle = w.setTimeout(probe, 600);
    }
    return () => {
      cancelled = true;
      if (idleHandle !== null && typeof w.cancelIdleCallback === 'function') {
        w.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== null) {
        w.clearTimeout(timeoutHandle);
      }
    };
  }, [videoSrc]);

  return (
    <>
      <img
        src={posterSrc}
        alt={posterAlt}
        fetchPriority="high"
        decoding="async"
        loading="eager"
        width={1920}
        height={1080}
        className={className}
        aria-hidden={posterAlt === '' ? true : undefined}
      />
      {videoOk && videoSrc && (
        <video
          ref={videoRef}
          src={videoSrc}
          poster={posterSrc}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onCanPlay={() => setVideoVisible(true)}
          onError={() => {
            setVideoOk(false);
            setVideoVisible(false);
          }}
          onStalled={() => setVideoVisible(false)}
          className={`${className ?? ''} absolute inset-0 transition-opacity duration-700 ${
            videoVisible ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default HeroMedia;
